import { useCreateApiToken } from '@/queries/api-tokens';
import { getErrorMessage } from '@/utils';
import CopyOutlined from '@ant-design/icons/CopyOutlined';
import EyeOutlined from '@ant-design/icons/EyeOutlined';
import Alert from 'antd/es/alert';
import App from 'antd/es/app';
import Button from 'antd/es/button';
import DatePicker from 'antd/es/date-picker';
import Form from 'antd/es/form';
import Input from 'antd/es/input';
import Modal from 'antd/es/modal';
import Radio from 'antd/es/radio';
import Typography from 'antd/es/typography';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';

import type { CreateApiTokenRequest } from '@mai/platform-api/src/routes/api-tokens.schema';

const { Text } = Typography;

const TokenDisplay = styled.div`
  ${({ theme }) => `
  background: ${theme.colorFillAlter};
  border: 1px solid ${theme.colorBorder};
  border-radius: ${theme.borderRadius}px;
  padding: ${theme.paddingMD}px;
  margin: ${theme.marginMD}px 0;
  font-family: ${theme.fontFamilyCode || 'monospace'};
  font-size: ${theme.fontSizeSM}px;
  word-break: break-all;
  position: relative;
  color: ${theme.colorText};
`}
`;

const CopyButton = styled(Button)`
  ${({ theme }) => `
  position: absolute;
  top: ${theme.paddingXS}px;
  right: ${theme.paddingXS}px;
`}
`;

const RoleDescription = styled.div`
  ${({ theme }) => `
  font-size: ${theme.fontSizeSM}px;
  color: ${theme.colorTextSecondary};
  margin-top: ${theme.marginXXS}px;
`}
`;

interface CreateTokenModalProps {
  open: boolean;
  onCancel: () => void;
  teamId: string;
}

interface FormValues {
  name: string;
  role: 'admin' | 'member' | 'readonly';
  expiresAt?: dayjs.Dayjs;
}

export const CreateTokenModal: React.FC<CreateTokenModalProps> = ({
  open,
  onCancel,
  teamId,
}) => {
  const { message } = App.useApp();
  const theme = useTheme();
  const [form] = Form.useForm<FormValues>();
  const createTokenMutation = useCreateApiToken();
  const [createdToken, setCreatedToken] = useState<string | null>(null);
  const [showToken, setShowToken] = useState(false);

  useEffect(() => {
    if (!open) {
      setCreatedToken(null);
      setShowToken(false);
    } else if (!createdToken) {
      form.resetFields();
    }
  }, [open, form, createdToken]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const requestData: CreateApiTokenRequest = {
        teamId,
        name: values.name,
        role: values.role,
        expiresAt: values.expiresAt ? values.expiresAt.toDate() : undefined,
      };

      const response = await createTokenMutation.mutateAsync(requestData);
      setCreatedToken(response.token);
      setShowToken(true);
      void message.success('API token created successfully');
    } catch (error) {
      void message.error(getErrorMessage(error, 'Failed to create API token'));
    }
  };

  const handleCopyToken = async () => {
    if (createdToken) {
      try {
        await navigator.clipboard.writeText(createdToken);
        void message.success('Token copied to clipboard');
      } catch (error) {
        void message.error('Failed to copy token');
      }
    }
  };

  const handleClose = () => {
    if (createdToken) {
      Modal.confirm({
        title: 'Close without saving?',
        content:
          'This is your only chance to copy the token. Are you sure you want to close?',
        okText: 'Close',
        cancelText: 'Stay',
        onOk: onCancel,
      });
    } else {
      onCancel();
    }
  };

  return (
    <Modal
      title="Create API Token"
      open={open}
      onCancel={handleClose}
      width={600}
      footer={
        createdToken ? (
          <Button type="primary" onClick={onCancel}>
            Done
          </Button>
        ) : (
          [
            <Button key="cancel" onClick={onCancel}>
              Cancel
            </Button>,
            <Button
              key="create"
              type="primary"
              onClick={handleSubmit}
              loading={createTokenMutation.isPending}
            >
              Create Token
            </Button>,
          ]
        )
      }
    >
      {createdToken ? (
        <div>
          <Alert
            type="warning"
            message="Save your token"
            description="This is the only time you'll see this token. Copy it now and store it securely."
            showIcon
            style={{ marginBottom: `${theme.margin}px` }}
          />

          <Text strong>Your new API token:</Text>
          <TokenDisplay>
            <CopyButton
              type="text"
              icon={<CopyOutlined />}
              onClick={handleCopyToken}
              title="Copy to clipboard"
            />
            {showToken ? (
              <div
                style={{
                  paddingRight: `${theme.paddingXL + theme.marginSM}px`,
                }}
              >
                {createdToken}
              </div>
            ) : (
              <div
                style={{
                  paddingRight: `${theme.paddingXL + theme.marginSM}px`,
                }}
              >
                {'•'.repeat(50)}
                <Button
                  type="link"
                  icon={<EyeOutlined />}
                  onClick={() => setShowToken(true)}
                  style={{ marginLeft: `${theme.marginXS}px` }}
                >
                  Show
                </Button>
              </div>
            )}
          </TokenDisplay>

          <Alert
            type="info"
            message="Security Tips"
            description={
              <ul style={{ margin: 0, paddingLeft: `${theme.padding}px` }}>
                <li>
                  Store this token in a secure location (password manager,
                  environment variables)
                </li>
                <li>Never commit this token to version control</li>
                <li>Rotate tokens regularly for security</li>
                <li>Use the minimum required role for your use case</li>
              </ul>
            }
            showIcon
          />
        </div>
      ) : (
        <Form form={form} layout="vertical" initialValues={{ role: 'member' }}>
          <Form.Item
            name="name"
            label="Token Name"
            rules={[
              { required: true, message: 'Please enter a token name' },
              { min: 1, max: 255, message: 'Name must be 1-255 characters' },
            ]}
          >
            <Input placeholder="e.g., Production API Access, CI/CD Pipeline" />
          </Form.Item>

          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: 'Please select a role' }]}
          >
            <Radio.Group>
              <Radio value="readonly">
                <div>
                  <div style={{ fontWeight: 500 }}>Read-only</div>
                  <RoleDescription>
                    Can only read data, cannot make changes
                  </RoleDescription>
                </div>
              </Radio>
              <Radio value="member">
                <div>
                  <div style={{ fontWeight: 500 }}>Member</div>
                  <RoleDescription>
                    Can read and modify team resources
                  </RoleDescription>
                </div>
              </Radio>
              <Radio value="admin">
                <div>
                  <div style={{ fontWeight: 500 }}>Admin</div>
                  <RoleDescription>
                    Full access including team management and API tokens
                  </RoleDescription>
                </div>
              </Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item name="expiresAt" label="Expiration (Optional)">
            <DatePicker
              showTime
              placeholder="Select expiration date"
              disabledDate={(current) => current && current < dayjs()}
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Alert
            type="info"
            message="Token Security"
            description="API tokens provide full access according to their role. Keep them secure and rotate them regularly."
            showIcon
          />
        </Form>
      )}
    </Modal>
  );
};
